import { Box, Center, Paper, Stack, Text } from '@mantine/core';
import zeusLogo from '@assets/image_1769186465013.png';

interface ZeusLoaderProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  text?: string;
  fullPage?: boolean;
  withBackground?: boolean;
}

const sizeMap = {
  sm: { logo: 32, dot: 4, gap: 4 },
  md: { logo: 48, dot: 5, gap: 5 },
  lg: { logo: 64, dot: 6, gap: 6 },
  xl: { logo: 80, dot: 8, gap: 8 }
};

export default function ZeusLoader({ 
  size = 'md', 
  text,
  fullPage = false,
  withBackground = false
}: ZeusLoaderProps) {
  const dimensions = sizeMap[size];
  
  const content = (
    <Stack gap={dimensions.gap * 2} align="center">
      <Box
        style={{
          position: 'relative',
          width: dimensions.logo,
          height: dimensions.logo
        }}
      >
        <Box
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: dimensions.logo + 10,
            height: dimensions.logo + 10,
            borderRadius: '50%',
            background: 'linear-gradient(45deg, rgba(47, 50, 137, 0.2), rgba(64, 38, 106, 0.2))',
            animation: 'zeusLoaderPulse 2s ease-in-out infinite'
          }}
        />
        
        <Box
          style={{
            position: 'relative',
            width: dimensions.logo,
            height: dimensions.logo,
            borderRadius: '50%',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'zeusLoaderSpin 3s linear infinite',
            boxShadow: '0 4px 16px rgba(47, 50, 137, 0.25)',
            background: 'white'
          }}
        >
          <img 
            src={zeusLogo} 
            alt="Loading" 
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              animation: 'zeusLoaderSpinReverse 3s linear infinite'
            }}
          />
        </Box>
      </Box>

      <Box display="flex" style={{ gap: dimensions.gap, justifyContent: 'center' }}>
        {[0, 0.2, 0.4].map((delay, i) => (
          <Box
            key={i}
            style={{
              width: dimensions.dot,
              height: dimensions.dot,
              borderRadius: '50%',
              background: 'linear-gradient(45deg, #2F3289, #40266A)',
              animation: `zeusLoaderDot 1.4s ease-in-out infinite ${delay}s`
            }}
          />
        ))}
      </Box>

      {text && (
        <Text 
          size={size === 'sm' ? 'xs' : 'sm'} 
          c="dimmed" 
          fw={500}
          ta="center"
        >
          {text}
        </Text>
      )}

      <style>{`
        @keyframes zeusLoaderPulse {
          0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
        }
        @keyframes zeusLoaderSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes zeusLoaderSpinReverse {
          from { transform: rotate(0deg); }
          to { transform: rotate(-360deg); }
        }
        @keyframes zeusLoaderDot {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </Stack>
  );

  if (fullPage) {
    return (
      <Center style={{ minHeight: '100vh', background: withBackground ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' : 'transparent' }}>
        {withBackground ? (
          <Paper
            shadow="md"
            p="xl"
            radius="lg"
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(47, 50, 137, 0.1)'
            }}
          >
            {content}
          </Paper>
        ) : content}
      </Center>
    );
  }

  return (
    <Center py="xl">
      {content}
    </Center>
  );
}
